<template>
    <div class="project-voting">
        <div class="stake-info alert" :class="stakeAlertClass" role="alert">
            <template v-if="userStake > 0">
                <strong>Your Current Stake: {{ userStake }} compassSOL</strong>
                <p class="mb-0">The more compassSOL you stake, the more powerful your votes become!</p>
            </template>
            <template v-else>
                <strong>No Stake Found</strong>
                <p class="mb-0">
                    Add a wallet with compassSOL stake to increase your voting power!
                    <a href="/account" class="alert-link">Go to Account Settings</a>
                </p>
            </template>
        </div>
        <div class="project-selector mb-3">
            <label class="form-label">Add Project to Vote</label>
            <vue-multiselect
                v-model="selectedProject"
                :options="availableProjects"
                :searchable="true"
                :close-on-select="true"
                :show-labels="false"
                placeholder="Search for a project"
                track-by="id"
                label="name"
                @select="addProject"
                class="project-multiselect"
            >
                <template #option="{ option }">
                    <img v-if="option.logo" :src="option.logo" class="me-2" style="width: 20px; height: 20px;">
                    {{ option.name }}
                </template>
            </vue-multiselect>
        </div>

        <div class="voted-projects">
            <transition-group name="vote-list">
                <div v-for="vote in displayVotes" :key="vote.project.id" class="voted-project mb-3">
                    <div class="d-flex align-items-center justify-content-between mb-2">
                        <div class="d-flex align-items-center">
                            <img v-if="vote.project.logo" :src="vote.project.logo" class="me-2"
                                 style="width: 24px; height: 24px;">
                            <span>{{ vote.project.name }} ({{ vote.percentage.toFixed(1) }}%)</span>
                        </div>
                        <button @click="removeVote(vote.project.id)" class="btn btn-sm btn-outline-danger">
                            <el-icon>
                                <Delete/>
                            </el-icon>
                        </button>
                    </div>
                    <el-slider
                        v-model="vote.percentage"
                        :min="0"
                        :max="100"
                        :step="0.1"
                        :format-tooltip="(val) => `${val.toFixed(1)}%`"
                        @input="handleSliderInput(vote.project.id)"
                        @change="handleSliderChange(vote.project.id)"
                    />
                </div>
            </transition-group>
        </div>

        <div v-if="votes.length" class="d-flex justify-content-between align-items-center mt-3">
            <div>
                Total: {{ totalPercentage }}%
            </div>
            <button
                @click="saveVotes"
                class="btn btn-primary"
                :disabled="!isValidTotal"
            >
                Save Votes
            </button>
        </div>
    </div>
</template>

<script>
import VueMultiselect from 'vue-multiselect'
import {ElMessage} from 'element-plus'

export default {
    components: {
        VueMultiselect,
    },

    props: {
        showingModal: {
            type: Boolean,
            default: false
        },
        preselectedProject: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            availableProjects: [],
            selectedProject: null,
            votes: [],
            loading: false,
            isSliding: false,
            userStake: 0
        }
    },

    computed: {
        normalizedVotes() {
            const total = this.votes.reduce((sum, vote) => sum + vote.percentage, 0)
            if (total === 0) return this.votes
            const ratio = 100 / total
            return this.votes.map(vote => ({
                ...vote,
                percentage: Math.max(0, Math.min(100, parseFloat((vote.percentage * ratio).toFixed(1))))
            }))
        },


        stakeAlertClass() {
            return this.userStake > 0 ? 'alert-info' : 'alert-warning'
        },
        totalPercentage() {
            const total = this.votes.reduce((sum, vote) => sum + vote.percentage, 0)
            return total === 0 ? 0 : 100
        },

        isValidTotal() {
            return this.votes.length > 0
        },

        displayVotes() {
            // Create a new sorted array to avoid mutating the original
            const sorted = [...this.votes]
            sorted.sort((a, b) => b.percentage - a.percentage)

            // During sliding, return the current array to prevent jumping
            if (this.isSliding) {
                return this.votes
            }

            // After sliding completes, update the votes array to match the sorted order
            this.votes = [...sorted]
            return sorted
        }
    },

    async created() {
        await this.loadProjects()
        await this.loadExistingVotes()
        await this.loadUserStake()
        if (this.preselectedProject && !this.votes.find(v => v.project.id === this.preselectedProject.id)) {

            this.addProject(this.preselectedProject)
        }
    },

    mounted() {

    },

    methods: {
        async loadProjects() {
            try {
                const response = await axios.get('/api/projects')
                let projects = await response.data
                console.log('got projects', projects)
                this.availableProjects = projects
            } catch (error) {
                console.error('Error loading projects:', error)
            }
        },

        async loadExistingVotes() {
            if (!voteStore.state.loaded) {
                await voteStore.loadUserVotes()
            }
            this.votes = voteStore.getUserVotes().map(v => ({
                project: v.project,
                percentage: parseFloat(v.percentage)
            }))
        },

        addProject(project) {
            if (!this.votes.find(v => v.project.id === project.id)) {
                this.votes.push({
                    project,
                    percentage: 0
                })
                this.selectedProject = null
            }
        },

        removeVote(projectId) {
            const index = this.votes.findIndex(v => v.project.id === projectId)
            if (index > -1) {
                const removedPercentage = this.votes[index].percentage
                this.votes.splice(index, 1)

                // Redistribute removed percentage
                if (this.votes.length > 0) {
                    const distribution = removedPercentage / this.votes.length
                    this.votes.forEach(vote => {
                        vote.percentage += distribution
                    })
                }
            }
        },

        handleSliderInput(changedProjectId) {
            this.isSliding = true
            this.adjustOtherVotes(changedProjectId)
        },

        handleSliderChange(changedProjectId) {
            this.adjustOtherVotes(changedProjectId)
            // Add small delay before updating sort order
            setTimeout(() => {
                this.isSliding = false
            }, 100)
        },

        adjustOtherVotes(changedProjectId) {
            const changedVote = this.votes.find(v => v.project.id === changedProjectId)
            const otherVotes = this.votes.filter(v => v.project.id !== changedProjectId)

            if (otherVotes.length === 0) {
                changedVote.percentage = 100
                return
            }

            const totalOtherPercentages = otherVotes.reduce((sum, vote) => sum + vote.percentage, 0)
            if (totalOtherPercentages === 0) return

            const remaining = 100 - changedVote.percentage
            const ratio = remaining / totalOtherPercentages

            otherVotes.forEach(vote => {
                vote.percentage = Math.max(0, Math.min(100, parseFloat((vote.percentage * ratio).toFixed(1))))
            })
        },

        async saveVotes() {
            if (!this.isValidTotal) return

            this.loading = true
            try {
                const response = await fetch('/api/votes', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
                    },
                    body: JSON.stringify({
                        votes: this.normalizedVotes.map(v => ({
                            project_id: v.project.id,
                            percentage: v.percentage
                        }))
                    })
                })

                if (response.ok) {
                    ElMessage({
                        message: 'Votes saved successfully!',
                        type: 'success',
                    })
                    this.$emit('votes-saved')
                } else {
                    const error = await response.json()
                    throw new Error(error.error || 'Failed to save votes')
                }
            } catch (error) {
                console.error('Error saving votes:', error)
                ElMessage({
                    message: error.message || 'Failed to save votes',
                    type: 'error',
                })
            } finally {
                this.loading = false
            }
        },

        async loadUserStake() {
            try {
                const response = await fetch('/api/user/stake')
                const data = await response.json()
                this.userStake = data.stake
            } catch (error) {
                console.error('Error fetching user stake:', error)
            }
        }
    }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>

<style>
.el-message {
    z-index: 9999999999 !important;
}

.project-voting {
    margin: 0 auto;
}

.project-selector {
    position: relative;
    /*z-index: 3000;*/
}

.project-multiselect {
    /*z-index: 3000;*/
}

.vue-multiselect {
    /*z-index: 3000 !important;*/
}

.voted-project {
    background: #f8f9fa;
    padding: 1rem;
    border-radius: 0.5rem;
}

.el-slider {
    width: 100%;
    margin: 10px 0;
}

.vote-list-move {
    transition: transform 0.5s ease;
}

.vote-list-enter-active,
.vote-list-leave-active {
    transition: all 0.5s ease;
}

.vote-list-enter-from,
.vote-list-leave-to {
    opacity: 0;
    transform: translateY(30px);
}
</style>
